/**
 * Product Priorities Table Component
 *
 * Displays product-level AEO priorities aggregated from URL data.
 * Shows enrichment coverage and variant information.
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger
} from '@/components/ui/tooltip';
import {
  Package, Download, Bot, ChevronLeft, ChevronRight, Info,
  ExternalLink, Layers
} from 'lucide-react';

import {
  getProductPriorities,
  exportPriorities,
  downloadCsv,
  ProductPriority,
} from '@/services/aeoPrioritiesService';

interface Props {
  siteId: string;
}

export function ProductPrioritiesTable({ siteId }: Props) {
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [priorities, setPriorities] = useState<ProductPriority[]>([]);
  const [page, setPage] = useState(0);
  const [limit] = useState(25);
  const [excludeEnriched, setExcludeEnriched] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [categories, setCategories] = useState<string[]>([]);

  useEffect(() => {
    loadPriorities();
  }, [siteId, page, excludeEnriched, categoryFilter]);

  const loadPriorities = async () => {
    setLoading(true);
    try {
      const result = await getProductPriorities(siteId, {
        limit,
        offset: page * limit,
        excludeEnriched,
        category: categoryFilter !== 'all' ? categoryFilter : undefined,
      });
      setPriorities(result.priorities);

      // Extract unique categories
      const uniqueCategories = [...new Set(result.priorities
        .map(p => p.context.category)
        .filter((c): c is string => c !== null)
      )];
      if (uniqueCategories.length > 0 && categories.length === 0) {
        setCategories(uniqueCategories);
      }
    } catch (err) {
      console.error('Failed to load product priorities:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await exportPriorities(siteId, 'product');
      downloadCsv(blob, `aeo-product-priorities-${siteId}.csv`);
    } catch (err) {
      console.error('Failed to export:', err);
    } finally {
      setExporting(false);
    }
  };

  const getCoverageColor = (coverage: number): string => {
    if (coverage >= 100) return 'bg-green-500';
    if (coverage >= 50) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  if (loading && priorities.length === 0) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Product Priorities
            </CardTitle>
            <CardDescription className="mt-1">
              Products ranked by potential AEO impact. Includes variant aggregation and enrichment coverage.
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={exporting}
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4 mt-4">
          <div className="flex items-center gap-2">
            <Checkbox
              id="excludeEnrichedProducts"
              checked={excludeEnriched}
              onCheckedChange={(checked) => setExcludeEnriched(checked === true)}
            />
            <label htmlFor="excludeEnrichedProducts" className="text-sm cursor-pointer">
              Hide fully enriched
            </label>
          </div>

          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="All categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {categories.map(cat => (
                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>

      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[35%]">Product</TableHead>
              <TableHead className="w-[100px] text-center">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger className="flex items-center gap-1 justify-center">
                      Score
                      <Info className="h-3 w-3" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p className="font-semibold mb-1">Product Score Components:</p>
                      <ul className="text-xs space-y-1">
                        <li>30% Bot Visits (aggregated)</li>
                        <li>25% AI Bot Share</li>
                        <li>20% Category Importance</li>
                        <li>10% Variant Boost</li>
                        <li>5% Sitemap Position</li>
                        <li>10% Enrichment Bonus</li>
                      </ul>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </TableHead>
              <TableHead className="text-center">URLs</TableHead>
              <TableHead className="text-center">Bot Visits</TableHead>
              <TableHead className="text-center">AI Share</TableHead>
              <TableHead>Category</TableHead>
              <TableHead className="text-center">Coverage</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {priorities.map((priority, index) => (
              <TableRow key={priority.productName} className={loading ? 'opacity-50' : ''}>
                <TableCell>
                  <div className="font-medium truncate max-w-[300px]">
                    {priority.productName}
                  </div>
                  {priority.context.sampleUrls.length > 0 && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="link" size="sm" className="h-auto p-0 text-xs text-muted-foreground">
                            {priority.metrics.urlCount} URLs
                            <ExternalLink className="h-3 w-3 ml-1" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="max-w-lg">
                          <p className="font-semibold mb-1">Sample URLs:</p>
                          <ul className="text-xs space-y-1">
                            {priority.context.sampleUrls.slice(0, 3).map((url, i) => (
                              <li key={i} className="font-mono truncate">{url}</li>
                            ))}
                          </ul>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </TableCell>

                <TableCell>
                  <div className="flex flex-col items-center gap-1">
                    <span className="font-bold text-lg">{priority.priorityScore.toFixed(1)}</span>
                    <Progress
                      value={priority.priorityScore}
                      className="h-1.5 w-16"
                    />
                  </div>
                </TableCell>

                <TableCell className="text-center">
                  <Badge variant="secondary" className="gap-1">
                    <Layers className="h-3 w-3" />
                    {priority.metrics.urlCount}
                  </Badge>
                </TableCell>

                <TableCell className="text-center">
                  <div className="flex items-center justify-center gap-1">
                    <Bot className="h-3 w-3 text-muted-foreground" />
                    <span>{priority.metrics.totalBotVisits30d.toLocaleString()}</span>
                  </div>
                </TableCell>

                <TableCell className="text-center">
                  {priority.metrics.aiBotVisits30d > 0 ? (
                    <Badge variant="secondary" className="bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">
                      {((priority.metrics.aiBotVisits30d / priority.metrics.totalBotVisits30d) * 100).toFixed(0)}%
                    </Badge>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </TableCell>

                <TableCell>
                  {priority.context.category ? (
                    <Badge variant="outline" className="truncate max-w-[100px]">
                      {priority.context.category}
                    </Badge>
                  ) : (
                    <span className="text-muted-foreground text-sm">-</span>
                  )}
                </TableCell>

                <TableCell>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="flex items-center gap-2">
                          <Progress
                            value={priority.metrics.enrichmentCoveragePct}
                            className="h-2 w-16"
                          />
                          <span className="text-sm font-medium">
                            {priority.metrics.enrichmentCoveragePct.toFixed(0)}%
                          </span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        {priority.metrics.enrichedUrlCount} of {priority.metrics.urlCount} URLs enriched
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </TableCell>
              </TableRow>
            ))}

            {priorities.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  No product priorities found. Products are identified from page semantic analysis.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        {priorities.length > 0 && (
          <div className="flex items-center justify-between mt-4">
            <div className="text-sm text-muted-foreground">
              Showing {page * limit + 1} - {page * limit + priorities.length}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0 || loading}
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => p + 1)}
                disabled={priorities.length < limit || loading}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
